import { useState, useEffect, useRef } from 'react';
import { Col, FloatingLabel, Nav, Row, Tab, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import Badge from '../../../components/base/Badge';
import { useMainLayoutContext } from '../../../providers/MainLayoutProvider';
import BiasDetection from '../Bias/BiasDetection';
import Dashboard from '../Dashboard/Dashboard';
import Fairness from '../Bias/Fairness';
import PromptInjectionPDC from '../PromptInjectionPDC/PromptInjectionPDC';
import ExplainabilityTables from '../Bias/Explainability';
import AuditReport from '../Bias/AuditReport';
import AISafetyTable from '../AISafety/AISafetyTable';
import { useGetProjectResultsQuery, useGetProjectListsQuery } from './data-access/project.api';
import { Project } from '@app/shared/types/project.type';
import { useDispatch } from 'react-redux';
import { setProjectResults } from './data-access/project.reducer';
import { Box, Chip, Button } from '@mui/material';
import { SentryErrorBoundary } from '../../../components/errors/SentryErrorBoundary';
import { useSentryProjectScope } from '../../../hooks/useSentryProjectScope';

const ProjectDetails = () => {
  const { setContentClass } = useMainLayoutContext();
  const dispatch = useDispatch();
  const biasDetectionRef = useRef<import('../Bias/BiasDetection').BiasDetectionRef | null>(null);
  const explainabilityRef = useRef<import('../Bias/Explainability').ExplainabilityRef | null>(null);
  const [biasVersionCounts, setBiasVersionCounts] = useState({ ml: 0, llm: 0 });
  const [explainabilityVersionCount, setExplainabilityVersionCount] = useState(0);
  const [explainabilityViewAllRequested, setExplainabilityViewAllRequested] = useState(false);

  const userId = localStorage.getItem('userId') || '';
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('dashboard');

  // Use RTK Query instead of Redux Saga
  const environment = localStorage.getItem('SELECTED_ENV') || 'DEV';
  const { data: projectList = [] } = useGetProjectListsQuery(
    { userId, environment },
    {
      skip: !userId // Skip if no userId
    }
  );
  // Set selected project to first project in list
  useEffect(() => {
    if (projectList.length > 0 && !selectedProjectId) {
      setSelectedProjectId(projectList[0].projectId);
    }
  }, [projectList, selectedProjectId]);

  // Set layout class
  useEffect(() => {
    setContentClass('px-0 pt-navbar');
    return () => setContentClass('');
  }, []);

  const shouldFetch = Boolean(selectedProjectId);

  const {
    data: projectResultsData,
    isFetching,
    isError
  } = useGetProjectResultsQuery(selectedProjectId!, {
    skip: !shouldFetch
  });
  useEffect(() => {
    if (projectResultsData) {
      dispatch(setProjectResults(projectResultsData));
    }
  }, [projectResultsData, dispatch]);
  // Handle dropdown change
  const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newProjectId = e.target.value;
    console.log('PROJECT ID', newProjectId);
    setSelectedProjectId(newProjectId);
  };

  const renderTabContent = () => {
    if (isFetching) {
      return (
        <div className="text-center py-5">
          <h5 className="text-muted">Loading project data...</h5>
        </div>
      );
    }

    if (projectResultsData?.error) {
      return (
        <div className="text-center py-5">
          <h5 className="text-danger">No project results found.</h5>
          <p>{projectResultsData.error}</p>
        </div>
      );
    }

    if (isError) {
      return (
        <div className="text-center py-5">
          <h5 className="text-danger">Something went wrong.</h5>
        </div>
      );
    }

    // Render actual tabs content
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard />;
      case 'bias':
        return (
          <BiasDetection
            ref={biasDetectionRef}
            onVersionsReady={(mlCount, llmCount) => {
              setBiasVersionCounts({ ml: mlCount, llm: llmCount });
            }}
            projectTitle={selectedProject?.title}
          />
        );
      case 'fairness':
        return <Fairness />;
      case 'explainability':
        return (
          <ExplainabilityTables
            ref={explainabilityRef}
            onVersionsReady={({ total }) => setExplainabilityVersionCount(total)}
            openHistoryOnMount={explainabilityViewAllRequested}
            onOpenHistoryConsumed={() => setExplainabilityViewAllRequested(false)}
          />
        );
      case 'abuse':
        return <AISafetyTable />;
      case 'prompt_injection':
        return <PromptInjectionPDC />;
      case 'audit_report':
        return <AuditReport projectId={selectedProjectId || undefined} />;
      default:
        return null;
    }
  };

  const selectedProject = projectList.find((item: Project) => item.projectId === selectedProjectId);

  useSentryProjectScope({
    projectId: selectedProjectId,
    projectTitle: selectedProject?.title,
    activeTab,
    environment,
    route: 'details'
  });

  // Persist project ID globally
  useEffect(() => {
    if (selectedProjectId) {
      localStorage.setItem('projectId', selectedProjectId);
    }
  }, [selectedProjectId]);

  return (
    <Row className="g-0">
      <Col xs={12} xxl={12} className="px-0 bg-body">
        <div className="px-4 px-lg-6 pt-6 pb-9">
          <div className="mb-3">
            <Row className="align-items-center justify-content-between g-3 mb-4">
              <Col md="auto">
                <h2 className="mb-0">{selectedProject?.title ?? 'Project'}</h2>
              </Col>
              <Col md="auto" className="d-flex">
                <FloatingLabel controlId="floatingSelectTask" label="Select Project">
                  <Form.Select onChange={handleProjectChange} value={selectedProjectId ?? ''}>
                    {projectList.map((item: Project) => (
                      <option key={item.projectId} value={item.projectId}>
                        {item.title}
                      </option>
                    ))}
                  </Form.Select>
                </FloatingLabel>
              </Col>
            </Row>

            <Badge
              variant="phoenix"
              bg="primary"
              iconPosition="end"
              icon={<FontAwesomeIcon icon={faEdit} className="ms-1" />}
            >
              Completed
            </Badge>
          </div>

          {/* View All Button - Above Tabs */}
          {(biasVersionCounts.llm > 0 || explainabilityVersionCount > 0) && (
            <Row className="mb-3">
              <Col xs={12}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    justifyContent: 'flex-end',
                    flexWrap: 'wrap'
                  }}
                >
                  {biasVersionCounts.llm > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={`LLM: ${biasVersionCounts.llm} versions`}
                        variant="outlined"
                        size="small"
                        sx={{ fontWeight: 500, borderColor: 'primary.main', color: 'primary.main' }}
                      />
                      <Button
                        onClick={() => biasDetectionRef.current?.handleHistoryOpen('llm')}
                        size="small"
                        variant="contained"
                        sx={{
                          backgroundColor: 'primary.main',
                          color: 'primary.contrastText',
                          textTransform: 'none',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          px: 2,
                          py: 0.75,
                          borderRadius: 1,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          '&:hover': { backgroundColor: 'primary.dark', boxShadow: '0 4px 8px rgba(0,0,0,0.15)' }
                        }}
                      >
                        View All
                      </Button>
                    </Box>
                  )}
                  {explainabilityVersionCount > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={`Explainability: ${explainabilityVersionCount} version${
                          explainabilityVersionCount !== 1 ? 's' : ''
                        }`}
                        variant="outlined"
                        size="small"
                        sx={{ fontWeight: 500, borderColor: 'secondary.main', color: 'secondary.main' }}
                      />
                      <Button
                        onClick={() => {
                          setActiveTab('explainability');
                          setExplainabilityViewAllRequested(true);
                        }}
                        size="small"
                        variant="contained"
                        sx={{
                          backgroundColor: 'secondary.main',
                          color: 'secondary.contrastText',
                          textTransform: 'none',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          px: 2,
                          py: 0.75,
                          borderRadius: 1,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          '&:hover': { backgroundColor: 'secondary.dark', boxShadow: '0 4px 8px rgba(0,0,0,0.15)' }
                        }}
                      >
                        View All
                      </Button>
                    </Box>
                  )}
                </Box>
              </Col>
            </Row>
          )}

          <Row className="gx-0 gx-sm-5 gy-8 mb-8">
            <Col xs={12} xl={12} xxl={12} className="pe-xl-0">
              <Tab.Container
                id="basic-tabs-example"
                activeKey={activeTab}
                onSelect={key => setActiveTab(key || 'dashboard')}
              >
                <Nav variant="underline" className="mb-3">
                  <Nav.Item>
                    <Nav.Link eventKey="dashboard">Dashboard</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="bias">Bias Detection</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="fairness">Fairness Metrics</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="explainability">Explainability</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="abuse">AI Safety</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="prompt_injection">Prompt Injection</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="audit_report">Audit Report</Nav.Link>
                  </Nav.Item>
                </Nav>
                <Tab.Content>
                  <SentryErrorBoundary name="project-details-tabs">{renderTabContent()}</SentryErrorBoundary>
                </Tab.Content>
              </Tab.Container>
            </Col>
          </Row>
        </div>
      </Col>
    </Row>
  );
};

export default ProjectDetails;
